import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  mapOfferCampaignError,
  useAuthSession,
  useStoreOfferCampaign,
  useTranslation,
  useUpdateOfferCampaign,
  type OfferCampaignErrorKey,
  type TranslationKey,
} from '@dukanti/shared';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  OfferCampaignFormPage,
  type OfferCampaignFormSubmit,
} from '@/features/offer-campaigns/components/offer-campaign-form-page';
import { SkeletonBar } from '@/features/products/components/skeleton-bar';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';

interface StoreContext {
  readonly storeId: string;
}

function toStoreContext(storeId: string | null): StoreContext | null {
  if (!storeId) return null;
  return { storeId };
}

export function OfferCampaignEditRoute() {
  const { t, dir } = useTranslation();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const authSession = useAuthSession(supabase);
  const { storeId } = useCurrentStore(authSession);
  const storeContext = useMemo(() => toStoreContext(storeId), [storeId]);
  const campaignQuery = useStoreOfferCampaign(supabase, {
    storeId: storeContext?.storeId ?? null,
    campaignId: campaignId ?? null,
  });
  const updateCampaign = useUpdateOfferCampaign(supabase);
  const [submitError, setSubmitError] = useState<OfferCampaignErrorKey | null>(null);

  async function handleSubmit(values: OfferCampaignFormSubmit): Promise<void> {
    setSubmitError(null);
    if (!storeContext || !campaignQuery.data) {
      setSubmitError('admin.offerCampaigns.errors.permissionDenied');
      return;
    }

    try {
      await updateCampaign.mutateAsync({
        campaign_id: campaignQuery.data.id,
        store_id: storeContext.storeId,
        title: values.title,
        short_description: values.short_description,
        starts_at: values.starts_at,
        ends_at: values.ends_at,
        is_active: values.is_active,
        variant_ids: values.variant_ids,
      });
      toast.success(t('admin.offerCampaigns.toasts.updated' as TranslationKey));
      navigate('/promotions/campaigns', { replace: true });
    } catch (err) {
      const mapped = mapOfferCampaignError(err);
      setSubmitError(mapped);
      toast.error(t(mapped as TranslationKey));
    }
  }

  return (
    <main dir={dir} data-testid="offer-campaign-edit-route" className="bg-background min-h-screen">
      {campaignQuery.isLoading ? (
        <OfferCampaignEditSkeleton loadingLabel={t('common.loading')} />
      ) : campaignQuery.isError || !storeContext ? (
        <OfferCampaignInlineError
          title={t('common.error')}
          description={t('common.errors.unknown' as TranslationKey)}
          retryLabel={t('common.retry')}
          onRetry={() => void campaignQuery.refetch()}
        />
      ) : campaignQuery.data === null ? (
        <OfferCampaignInlineError
          title={t('admin.offerCampaigns.errors.notFound' as TranslationKey)}
          description={t('admin.offerCampaigns.errors.notFound' as TranslationKey)}
          retryLabel={t('admin.offerCampaigns.form.back' as TranslationKey)}
          onRetry={() => navigate('/promotions/campaigns')}
        />
      ) : (
        <OfferCampaignFormPage
          mode="edit"
          storeId={storeContext.storeId}
          campaign={campaignQuery.data}
          isSubmitting={updateCampaign.isPending}
          errorKey={submitError}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/promotions/campaigns')}
        />
      )}
    </main>
  );
}

interface OfferCampaignInlineErrorProps {
  readonly title: string;
  readonly description: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
}

function OfferCampaignInlineError({
  title,
  description,
  retryLabel,
  onRetry,
}: OfferCampaignInlineErrorProps) {
  return (
    <div className="mx-auto max-w-lg px-6 py-16 text-center" role="alert">
      <div className="border-border bg-card shadow-admin-card rounded-lg border p-8">
        <AlertCircle className="text-destructive mx-auto mb-4 size-10" strokeWidth={1.5} />
        <h1 className="font-display text-foreground mb-2 text-xl">{title}</h1>
        <p className="text-muted-foreground mb-5 text-sm">{description}</p>
        <Button type="button" variant="outline" onClick={onRetry}>
          {retryLabel}
        </Button>
      </div>
    </div>
  );
}

function OfferCampaignEditSkeleton({ loadingLabel }: { readonly loadingLabel: string }) {
  return (
    <div
      className="mx-auto max-w-screen-md px-6 pb-32 pt-8"
      role="status"
      aria-busy="true"
      aria-live="polite"
      data-testid="offer-campaign-edit-skeleton"
    >
      <span className="sr-only">{loadingLabel}</span>
      <SkeletonBar className="mb-6 h-4 w-24" />
      <SkeletonBar className="mb-8 h-9 w-64" />
      <div className="border-border bg-card shadow-admin-card rounded-lg border p-6 sm:p-8">
        <SkeletonBar className="mb-5 h-10 w-full rounded-md" />
        <SkeletonBar className="mb-5 h-24 w-full rounded-md" />
        <SkeletonBar className="mb-5 h-10 w-full rounded-md" />
        <SkeletonBar className="h-44 w-full rounded-md" />
      </div>
    </div>
  );
}
